import React from 'react'
import PropTypes from 'prop-types'

import * as firebase from 'firebase'
import moment from 'moment'
import { connect } from 'react-redux'

import JobScreen from '../schedule/common/JobScreen.js'
import ReportHeader from './common/ReportHeader'
import PayrollReport from './payroll/PayrollReport'
import ServiceReport from './service/ServiceReport'
import TimeAllocationReport from './timeAllocation/TimeAllocationReport'
import ServiceReportExporter from './common/ServiceReportExporter'
import PayrollReportExporter from './common/PayrollReportExporter'

class Reports extends React.Component {

  static Title = {
    payroll: 'Payroll',
    serviceSummary: 'Service Summary',
    timeAllocation: 'Time Allocation'
  }

  static propTypes = {
    currentReportType: PropTypes.string,
    userId: PropTypes.string.isRequired
  }

  constructor(props) {
    super(props)
    this.state = {
      selectedWorkers: [],
      report: props.currentReportType,
      exportReport : false,
      settings: {},
      printReport: false
    }
    //Settings Resources
    this.settingsReportPath = ''
    this.settingsProcessor = null
  }

  componentWillMount() {
    this.initReportSettingsListener(this.props.currentReportType, this.props.userId)
  }

  componentWillReceiveProps(nextProps) {
    if (this.props.currentReportType !== nextProps.currentReportType
      || this.props.userId !== nextProps.userId) {
      this.detachReportSettingsListener()
      this.initReportSettingsListener(nextProps.currentReportType, nextProps.userId)
    }
  }

  componentWillUnmount() {
    this.detachReportSettingsListener()
  }

  initReportSettingsListener = (reportType, userId) => {
    if (reportType && userId) {
      const settingsReportBasePath = `/users/${userId}/settings/reports`
      switch (reportType) {
        case 'payroll':
          this.settingsReportPath = `${settingsReportBasePath}/payroll`
          this.settingsProcessor = this.payrollSettingsProcessor
          break
        case 'serviceSummary':
          this.settingsReportPath = `${settingsReportBasePath}/service`
          this.settingsProcessor = this.serviceSettingsProcessor
          break
        case 'timeAllocation':
          this.settingsReportPath = `${settingsReportBasePath}/timeAllocation`
          this.settingsProcessor = this.timeAllocationSettingsProcessor
          break
      }
      firebase.database().ref(this.settingsReportPath).on('value', this.settingsProcessor)
    }
  }

  detachReportSettingsListener = () => {
    if (this.settingsReportPath) {
      firebase.database().ref(this.settingsReportPath).off('value', this.settingsProcessor)
      this.settingsReportPath = ''
      this.settingsProcessor = null
    }
  }

  getCurrentDayReportDateRange = userSettings => {
    //Date Settings
    let startDate, endDate, currentDate

    //setting currentDate
    currentDate = moment().format('YYYY-MM-DD')

    if (!userSettings
        || !userSettings.startDate
        || !userSettings.endDate
        || !userSettings.currentDate) {
      //if missing any of the required fields, default to today
      startDate = currentDate
      endDate = startDate
    } else if (currentDate !== userSettings.currentDate) {
      //opening report for first time today. So default to today
      startDate = currentDate
      endDate = startDate
    } else {
      startDate = userSettings.startDate
      endDate = userSettings.endDate
    }
    return { startDate, endDate }
  }

  getCurrentWeekReportRange = userSettings => {
    //Date Settings
    let startDate, endDate, currentDate
    
    //setting currentDate
    currentDate = moment().format('YYYY-MM-DD')
    startDate = moment()
    startDate.startOf('week')
    endDate = moment(startDate).add(6, 'days')

    if (!userSettings
        || !userSettings.startDate
        || !userSettings.endDate
        || !userSettings.currentDate) {
      startDate = startDate.format('YYYY-MM-DD')
      endDate = endDate.format('YYYY-MM-DD')
    } else if (currentDate !== userSettings.currentDate) {
      //opening report for first time today. So default to this week
      startDate = startDate.format('YYYY-MM-DD')
      endDate = endDate.format('YYYY-MM-DD')
    } else {
      startDate = userSettings.startDate
      endDate = userSettings.endDate
    }
    return { startDate, endDate }
  }

  serviceSettingsProcessor = snapshot => {
    const dateRange = this.getCurrentDayReportDateRange(snapshot.val())
    this.renderIffNecessary('serviceSummary', dateRange)
  }

  payrollSettingsProcessor = snapshot => {
    const dateRange = this.getCurrentWeekReportRange(snapshot.val())
    this.renderIffNecessary('payroll', dateRange)
  }

  timeAllocationSettingsProcessor = snapshot => {
    const dateRange = this.getCurrentWeekReportRange(snapshot.val()) 
    this.renderIffNecessary('timeAllocation', dateRange)
  }

  saveSettings(field, value){
    firebase.database().ref(`${this.settingsReportPath}/${field}`).set(value)
  }

  renderIffNecessary = (reportType, dateRange) => {
    const startDate = dateRange.startDate
    const endDate = dateRange.endDate
    if (reportType !== this.state.reportType
      || startDate !== this.state.startDateStr
      || endDate !== this.state.endDateStr) {
      this.setState({
        report: reportType,
        startDateStr: startDate,
        endDateStr: endDate
      })
    }
  }

  handlePrintReport = () => {
    this.setState({ printReport: true })
  }

  handleDateChange = (startDateStr, endDateStr) => {
    // Get current date
    const currentDate = moment().format('YYYY-MM-DD')

    // Save to firebase
    this.saveSettings('startDate', startDateStr)
    this.saveSettings('endDate', endDateStr)
    this.saveSettings('currentDate', currentDate)

    this.setState({
      startDateStr,
      endDateStr
    })
  }

  handleSettingsChange = newSettings => {
    this.setState({ settings: newSettings })
  }

  handleWorkerSelectionChange = newSelectedWorkers => {
    this.setState({
      selectedWorkers: newSelectedWorkers
    })
  }

  onExport = () => {
    this.setState({ exportReport : true })
  }

  handleExportCompleted = () => {
    this.setState({ exportReport : false })
  }

  handlePrintReportCompleted = () => {
    this.setState({ printReport: false })
  }

  renderReport = () => {
    switch (this.state.report) {
      case 'payroll':
        return <PayrollReport
          startDateStr={this.state.startDateStr}
          endDateStr={this.state.endDateStr}
          selectedWorkers={this.state.selectedWorkers} />
      case 'serviceSummary':
        return <ServiceReport
          dateStr={this.state.startDateStr}
          selectedWorkers={this.state.selectedWorkers} />
      case 'timeAllocation':
        return <TimeAllocationReport
          startDateStr={this.state.startDateStr}
          endDateStr={this.state.endDateStr}
          settings={this.state.settings}
          printReport={this.state.printReport}
          onPrintCompleted={this.handlePrintReportCompleted}
          exportReport={this.state.exportReport}
          onExportCompleted={this.handleExportCompleted}
          selectedWorkers={this.state.selectedWorkers} />
      default:
        return null
    }
  }

  getReportExporter = () => {
    switch (this.state.report) {
      case 'payroll':
        return <PayrollReportExporter
          dateStr={this.state.startDateStr}
          selectedWorkers={this.state.selectedWorkers}
          startProcess={this.state.exportReport}
          onExportComplete={this.handleExportCompleted} />
      case 'serviceSummary':
        return <ServiceReportExporter
          dateStr={this.state.startDateStr}
          selectedWorkers={this.state.selectedWorkers}
          startProcess={this.state.exportReport}
          onExportComplete={this.handleExportCompleted} />
      default:
        return null
    }
  }

  render() {
    const exporter = this.getReportExporter()
    return (
      <div style={styles.container}>
        <ReportHeader
          report={this.state.report}
          startDateStr={this.state.startDateStr}
          endDateStr={this.state.endDateStr}
          singleDay={this.state.report === 'serviceSummary' || this.state.report === 'timeAllocation'}
          onCSVExport={this.onExport}
          onDateChange={this.handleDateChange}
          showTimePeriod={this.state.report === 'timeAllocation'}
          onSettingsChange={this.handleSettingsChange}
          onPrintReport={this.handlePrintReport}
          onWorkerSelectionChange={this.handleWorkerSelectionChange} />

        { exporter }
        { this.renderReport() }

        <JobScreen />
      </div>
    )
  }

}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'white',
    overflow: 'auto',
    flexGrow: 1
  }
}

function mapStateToProps(state){
  const props = {
    userId: state.firebase.userAuth.id
  }
  if (state.reports && state.reports.uiData) {
    props.currentReportType = state.reports.uiData.type
  }
  return props
}

export default connect(mapStateToProps)(Reports)



// WEBPACK FOOTER //
// ./src/reports/Reports.js